import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useDismiss from "@/hooks/useDismiss";
import GuestBreakdown from "@/model/GuestBreakdown";
import guestsUtils from "@/utils/guests";
import "@/components/GuestPicker/GuestPicker.scss";

type GuestCategory = keyof GuestBreakdown;

const CATEGORIES: GuestCategory[] = ["adults", "children", "infants"];
const NO_GUESTS: GuestBreakdown = { adults: 0, children: 0, infants: 0 };

interface GuestPickerProps {
  id: string;
  label: string;
  /** Null means "any number" (search only, see allowEmpty). */
  value: GuestBreakdown | null;
  onChange: (guests: GuestBreakdown | null) => void;
  /** Caps adults + children (infants don't take a place). */
  maxGuests?: number;
  /** The search can go back to "no guests chosen"; a booking always keeps one adult. */
  allowEmpty?: boolean;
  className?: string;
}

function GuestPicker({
  id,
  label,
  value,
  onChange,
  maxGuests = guestsUtils.MAX_GUESTS,
  allowEmpty = false,
  className,
}: GuestPickerProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setIsOpen(false), []);
  useDismiss(rootRef, isOpen, close);

  const current = value ?? NO_GUESTS;
  const counted = guestsUtils.countedGuests(current);
  const hasOthers = current.children > 0 || current.infants > 0;

  const min = (category: GuestCategory): number => {
    if (category !== "adults") {
      return 0;
    }
    // Children and infants never travel alone; an empty search is only allowed without them.
    return allowEmpty && !hasOthers ? 0 : 1;
  };

  const max = (category: GuestCategory): number => {
    if (category === "infants") {
      return guestsUtils.MAX_INFANTS;
    }
    return current[category] + Math.max(maxGuests - counted, 0);
  };

  const change = (category: GuestCategory, delta: number) => {
    const next = { ...current, [category]: current[category] + delta };
    // Adding a child or an infant first brings an adult along, like Airbnb.
    if (next.adults === 0 && (next.children > 0 || next.infants > 0)) {
      next.adults = 1;
    }
    const isEmpty = next.adults === 0 && next.children === 0 && next.infants === 0;
    onChange(isEmpty ? null : next);
  };

  return (
    <div
      ref={rootRef}
      className={`guest-picker${className ? ` ${className}` : ""}`}
    >
      <label id={`${id}-label`} htmlFor={id}>
        {label}
      </label>
      {/* Named by the label and the summary, so screen readers hear the current selection. */}
      <button
        id={id}
        type="button"
        className="guest-picker__toggle"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-labelledby={`${id}-label ${id}-summary`}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span
          id={`${id}-summary`}
          className={value ? undefined : "guest-picker__placeholder"}
        >
          {value ? guestsUtils.summarize(t, value) : t("guests.placeholder")}
        </span>
      </button>

      {isOpen && (
        <div className="guest-picker__panel" role="dialog" aria-label={label}>
          <p className="guest-picker__total" aria-live="polite">
            {value ? guestsUtils.summarize(t, value) : t("guests.placeholder")}
          </p>
          {CATEGORIES.map((category) => (
            <div key={category} className="guest-picker__row">
              <div>
                <p className="guest-picker__category">
                  {t(`guests.${category}`)}
                </p>
                <p className="guest-picker__hint">
                  {t(`guests.${category}Hint`)}
                </p>
              </div>
              <div className="guest-picker__stepper">
                <button
                  type="button"
                  aria-label={t("guests.decrease", {
                    category: t(`guests.${category}`),
                  })}
                  disabled={current[category] <= min(category)}
                  onClick={() => change(category, -1)}
                >
                  −
                </button>
                <span aria-live="polite">{current[category]}</span>
                <button
                  type="button"
                  aria-label={t("guests.increase", {
                    category: t(`guests.${category}`),
                  })}
                  disabled={current[category] >= max(category)}
                  onClick={() => change(category, 1)}
                >
                  +
                </button>
              </div>
            </div>
          ))}
          <p className="guest-picker__note">
            {maxGuests < guestsUtils.MAX_GUESTS
              ? t("guests.capacityNote", { count: maxGuests })
              : t("guests.infantsNote")}
          </p>
        </div>
      )}
    </div>
  );
}

export default GuestPicker;
export type { GuestPickerProps };
